// Per-route SEO metadata. Used server-side (server.ts / middleware.ts) to
// inject the right <title>/description/OG tags before HTML reaches the
// browser, so link previews on Discord/Facebook/X/Slack and Google's index
// show the right page instead of the generic homepage copy. Also used
// client-side (hooks/usePageMeta.ts) to keep the tab title in sync during
// SPA navigation.
//
// Each route carries both a Norwegian and an English variant. Which one
// applies is decided purely by the URL's "/en" prefix (see
// detectLanguageFromPathname below and index.tsx/i18n.ts on the client) -
// never a cookie or Accept-Language guess, so every URL serves exactly
// one deterministic language and Google can index both independently.

export interface PageMeta {
  title: string;
  description: string;
}

export type SeoLanguage = 'no' | 'en';

interface BilingualPageMeta {
  no: PageMeta;
  en: PageMeta;
}

const DEFAULT_META: BilingualPageMeta = {
  no: {
    title: "Skjeta.no - Den sykeste humoren",
    description:
      "Skjeta.no - Norges sykeste sted for humor, memes, og morsomme spill. Bli med i samfunnet og del dine beste vitser!",
  },
  en: {
    title: "Skjeta.no - The sickest humor",
    description:
      "Skjeta.no - Norway's sickest place for humor, memes, and fun games. Join the community and share your best jokes!",
  },
};

export const PAGE_META: Record<string, BilingualPageMeta> = {
  "/": DEFAULT_META,
  "/memes": {
    no: {
      title: "Memes - Skjeta.no",
      description: "Bla gjennom Norges sykeste memes og stem på dine favoritter på Skjeta.no.",
    },
    en: {
      title: "Memes - Skjeta.no",
      description: "Browse Norway's sickest memes and vote for your favorites on Skjeta.no.",
    },
  },
  "/news": {
    no: {
      title: "Nyheter - Skjeta.no",
      description: "Ferskeste skjeta-nyhetene, oppdatert daglig med AI-genererte overskrifter.",
    },
    en: {
      title: "News - Skjeta.no",
      description: "The freshest skjeta news, updated daily with AI-generated headlines.",
    },
  },
  "/games": {
    no: {
      title: "Spill - Skjeta.no",
      description: "Skjeta Flipp, Tinder, Quiz og Svele-Sprint - spill deg gjennom Norges sykeste minispill.",
    },
    en: {
      title: "Games - Skjeta.no",
      description: "Skjeta Flipp, Tinder, Quiz and Svele-Sprint - play through Norway's sickest mini-games.",
    },
  },
  "/leaderboard": {
    no: {
      title: "Ledertavle - Skjeta.no",
      description: "Se hvem som topper Skjeta-ledertavlen denne sesongen.",
    },
    en: {
      title: "Leaderboard - Skjeta.no",
      description: "See who's topping the Skjeta leaderboard this season.",
    },
  },
  "/skjetaperson": {
    no: {
      title: "Månedens Skjetaperson - Skjeta.no",
      description: "Møt månedens Skjetaperson - kåret av samfunnet på Skjeta.no.",
    },
    en: {
      title: "Skjetaperson of the Month - Skjeta.no",
      description: "Meet this month's Skjetaperson - crowned by the community on Skjeta.no.",
    },
  },
  "/confessions": {
    no: {
      title: "Skriftemål - Skjeta.no",
      description: "Anonyme skjeta-skriftemål fra samfunnet. Del ditt, eller bla gjennom andres.",
    },
    en: {
      title: "Confessions - Skjeta.no",
      description: "Anonymous skjeta confessions from the community. Share yours, or browse others'.",
    },
  },
  "/wiki": {
    no: {
      title: "Skjeta Wiki - Skjeta.no",
      description: "Alt du trenger å vite om Skjeta-universet, samlet på ett sted.",
    },
    en: {
      title: "Skjeta Wiki - Skjeta.no",
      description: "Everything you need to know about the Skjeta universe, all in one place.",
    },
  },
  "/rules": {
    no: {
      title: "Regler - Skjeta.no",
      description: "Reglene for Skjeta.no - les før du deltar.",
    },
    en: {
      title: "Rules - Skjeta.no",
      description: "The rules for Skjeta.no - read before you participate.",
    },
  },
  "/contact": {
    no: {
      title: "Kontakt - Skjeta.no",
      description: "Ta kontakt med Skjeta.no-teamet.",
    },
    en: {
      title: "Contact - Skjeta.no",
      description: "Get in touch with the Skjeta.no team.",
    },
  },
};

// Strips a leading "/en" (the English site's URL prefix) so the rest of
// this module can key PAGE_META purely by the bare, language-agnostic
// path.
export function stripLangPrefix(pathname: string): string {
  const stripped = pathname.replace(/^\/en(\/|$)/, '/');
  return stripped.length > 1 ? stripped.replace(/\/$/, '') || '/' : stripped;
}

export function detectLanguageFromPathname(pathname: string): SeoLanguage {
  return /^\/en(\/|$)/.test(pathname) ? 'en' : 'no';
}

export function resolvePageMeta(pathname: string, lang?: SeoLanguage): PageMeta {
  const basePath = stripLangPrefix(pathname);
  const resolvedLang = lang ?? detectLanguageFromPathname(pathname);
  const entry = PAGE_META[basePath] || DEFAULT_META;
  return entry[resolvedLang] || entry.no;
}
